import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import SEO from '../components/SEO';

const SECTIONS = [
  {
    n: '1',
    title: 'Parties to this Agreement',
    body: [
      'These Training Terms & Conditions ("Terms") form a binding contract between:',
      '(a) Jo Day, trading as Audio & Co. (a sole trader operating in England), registered correspondence address available on request, hereafter "Audio & Co.", "we", "us" or "our"; and',
      '(b) The individual or organisation purchasing a place on the Programme (defined below), hereafter "you", "Delegate", or "Customer".',
      'These Terms apply alongside our general Website Terms of Service and Privacy Policy. Where there is a conflict, these Training Terms take precedence for Programme purchases.',
    ],
  },
  {
    n: '2',
    title: 'What You Are Buying',
    body: [
      'You are purchasing a place on the "Build Your Own Website & Mobile App With AI" training programme (the "Programme") delivered by Audio & Co.',
      'The Programme comprises 6 modules delivered across approximately 6 weeks. Depending on the tier you purchase, your access includes some or all of the following:',
      '• Self-Paced (£295): All 6 module recordings (lifetime access), downloadable PDF workbook, private community access, periodic live group Q&A sessions, and step-by-step build checklists.',
      '• Live Cohort (£795): Everything in Self-Paced PLUS 6 × 1-hour live online teaching sessions with Jo Day plus an optional 1-hour Q&A clinic each week, one 1-to-1 site review call of 30 minutes, and lifetime access to all future updates.',
      '• VIP Done-With-You (£1,995): Everything in Live Cohort PLUS 3 × 60-minute 1-to-1 calls with Jo Day, direct WhatsApp access during the 6-week cohort, a personal launch site review by Jo, and one month of complimentary Done-For-You SEO (worth £295) following the cohort.',
      'The full description of each tier is published on https://audioandco.com/training at the time of your purchase and forms part of these Terms.',
    ],
  },
  {
    n: '3',
    title: 'Programme Duration and Delivery',
    body: [
      '3.1 The Programme runs for approximately 6 weeks with one 1-hour live teaching session per week, followed by an optional 1-hour Q&A clinic for delegates who want additional support that week (where applicable to your tier).',
      '3.2 Live sessions are delivered online via Zoom (or an equivalent platform of our choosing). All sessions are recorded and made available inside your Members Portal within 48 hours.',
      '3.3 We will publish cohort dates a minimum of 14 days in advance. You will be assigned to the next available cohort after your cooling-off period (clause 6) has expired. Members Portal login details will be emailed to you 48 hours before your first live session.',
      '3.4 Access to recordings, workbook, and community is for the lifetime of the Programme (we currently anticipate maintaining access for at least 3 years from your purchase date, though we make no guarantee beyond that).',
      '3.5 We reserve the right to substitute the live-session platform, reschedule individual sessions by up to 7 days, or change the delivery format from live to recorded in exceptional circumstances. We will give you as much notice as reasonably possible.',
    ],
  },
  {
    n: '4',
    title: 'What WE Are Responsible For',
    body: [
      '4.1 Delivering the modules listed in clause 2 to a professional standard.',
      '4.2 Providing access to recordings, workbook, and community within the timeframes stated.',
      '4.3 Responding to Programme questions posted in the official community within 5 working days during the active cohort period.',
      '4.4 Keeping your personal data secure in line with UK GDPR and our published Privacy Policy.',
      '4.5 Providing reasonable adjustments where you notify us in advance of an accessibility need we can practically accommodate.',
    ],
  },
  {
    n: '5',
    title: 'What YOU Are Responsible For',
    body: [
      '5.1 Attending live sessions (where applicable to your tier) and engaging with the materials. The Programme teaches you how to build your own website and mobile app — the onus is on you to do the work.',
      '5.2 All third-party costs required to apply what you learn, including but not limited to:',
      '— AI website builder subscription (typically around £20/month)',
      '— Domain name registration (typically £10-£15/year)',
      '— AI credits, hosting, image generation tools (typically £20-£100 depending on usage)',
      '— Optional integrations such as Stripe, Calendly, email marketing tools, payment processors, app store listings, or analytics products',
      '5.3 Providing your own computer, stable internet connection, and a Zoom-capable device for live sessions.',
      '5.4 Acting honestly and lawfully in the community. Harassment, hate speech, doxxing, or repeated disruption may result in removal from the cohort with no refund.',
      '5.5 Paying for the Programme in full and on time (see clauses 7 and 8).',
      '5.6 Acknowledging that outcomes vary based on your effort, your business, and your market. Audio & Co. makes no guarantee of specific income, traffic, ranking, customer acquisition, or business success as a result of the Programme.',
    ],
  },
  {
    n: '6',
    title: 'Cooling-Off Period and Refunds',
    body: [
      '6.1 In accordance with the Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013, you have a statutory right to cancel your purchase within 14 calendar days of payment ("Cooling-Off Period").',
      '6.2 Training will NOT begin during the Cooling-Off Period. Audio & Co. will not deliver any live session, 1-to-1 call, or release any workbook content to you until the Cooling-Off Period has expired.',
      '6.3 To cancel during the Cooling-Off Period, you must notify us in writing at hello@audioandco.com. We will refund 100% of monies paid within 14 days of receiving your cancellation notice.',
      '6.4 Once the Cooling-Off Period has expired and training has commenced (whether or not you have attended any session), the Programme is non-refundable. No partial refunds will be offered for missed sessions, change of mind, or business circumstances.',
      '6.5 By proceeding with payment AFTER the Cooling-Off Period, or by attending any live session, accessing any recording, or engaging with the workbook, you expressly acknowledge that you waive your statutory right to a refund in line with regulation 36 of the 2013 Regulations.',
    ],
  },
  {
    n: '7',
    title: 'Payment Terms',
    body: [
      '7.1 Pricing is published on the Training page and is inclusive of any applicable VAT (Audio & Co. is currently below the UK VAT threshold and does not charge VAT; this may change in future).',
      '7.2 Payment is in GBP via the payment provider published on the Training page (currently Wise). You authorise the payment provider to charge the stated amount.',
      '7.3 Full Payment: Payment in full is due at the time of purchase via the Buy Now button on the Training page.',
      '7.4 Payment Plans: At our discretion and on written request, we may offer a 2- or 3-instalment payment plan. Where granted:',
      '— The first instalment is due at the time of purchase.',
      '— Subsequent instalments are due 30 and 60 days after the first payment.',
      '— ALL instalments must be paid on or before their due date.',
      '— Payment plans are offered IN GOOD FAITH on the understanding that you are consuming our knowledge and know-how and remain liable for the full balance regardless of your continued engagement.',
    ],
  },
  {
    n: '8',
    title: 'Late Payment, Missed Payment and Debt Recovery',
    body: [
      '8.1 If any instalment under a payment plan is not received by its due date, the FULL OUTSTANDING BALANCE of the Programme fee becomes IMMEDIATELY DUE AND PAYABLE. We will issue a reminder within 3 working days of the missed instalment.',
      '8.2 If full payment of the outstanding balance is not received within 7 days of the reminder notice, we may at our discretion:',
      '(a) suspend or terminate your access to the Programme (recordings, community, workbook, live sessions, calls) without further notice;',
      '(b) apply a late payment fee of £40 per missed instalment, in line with the Late Payment of Commercial Debts (Interest) Act 1998 where you are a business customer, or a fair late fee where you are a consumer;',
      '(c) charge statutory interest on overdue balances at 8% above the Bank of England base rate (per the Act, where applicable);',
      '(d) instruct a debt collection agency, with the costs of recovery added to your outstanding balance.',
      '8.3 Small Claims Court: For any outstanding balance that remains unpaid 30 days after the missed instalment, we reserve the right to issue County Court (Money Claim Online / small claims) proceedings against you in England and Wales. You will be liable for the original debt, accrued interest, debt collection costs, and any court fees we incur.',
      '8.4 The above remedies are cumulative. Pursuing any one of them does not preclude pursuit of the others.',
    ],
  },
  {
    n: '9',
    title: 'Intellectual Property',
    body: [
      '9.1 All Programme materials — including video recordings, slides, workbook, written content, prompts, templates, frameworks, scripts, community posts authored by Jo Day, and the methodology taught — remain the intellectual property of Audio & Co.',
      '9.2 You are granted a personal, non-transferable, non-exclusive licence to use the materials FOR YOUR OWN business and learning.',
      '9.3 You may NOT:',
      '— Share, redistribute, re-sell, or publish any Programme material in whole or in part;',
      '— Record live sessions on your own device (the official recording is provided);',
      '— Use the materials to deliver a competing training programme;',
      '— Allow others (employees, family, friends) to access materials using your login.',
      '9.4 Breach of clause 9 is a material breach. We reserve the right to terminate your access without refund and pursue damages for loss caused by unauthorised distribution.',
      '9.5 Anything YOU create using what you learn — your website, your app, your content — belongs entirely to you.',
    ],
  },
  {
    n: '10',
    title: 'No Guarantee of Results',
    body: [
      '10.1 The Programme teaches a set of skills and tools. Audio & Co. makes NO REPRESENTATION OR WARRANTY as to:',
      '— Specific income, revenue, or profit you may generate;',
      '— Specific Google rankings, traffic levels, or visibility on AI search platforms;',
      '— Specific number of leads, enquiries, customers, or app downloads;',
      '— Speed at which any of the above may be achieved.',
      '10.2 Any examples, case studies, screenshots, or testimonials shown during the Programme illustrate what is possible, not what is typical or guaranteed.',
      '10.3 Your results depend on your effort, your business, your market, the choices you make, and factors outside our control (search engine algorithms, AI provider policies, regulatory changes, competition, economic conditions).',
    ],
  },
  {
    n: '11',
    title: 'Limitation of Liability',
    body: [
      '11.1 Nothing in these Terms excludes or limits our liability for:',
      '— Death or personal injury caused by our negligence;',
      '— Fraud or fraudulent misrepresentation;',
      '— Breach of your statutory consumer rights;',
      '— Any other liability which cannot be excluded by law.',
      '11.2 Subject to clause 11.1, our total aggregate liability to you under or in connection with the Programme, whether in contract, tort (including negligence), breach of statutory duty, or otherwise, shall be LIMITED TO THE TOTAL FEE YOU HAVE ACTUALLY PAID TO US under these Terms.',
      '11.3 We shall not be liable for indirect, consequential, special, or exemplary losses, including loss of business, loss of profit, loss of goodwill, or loss of data, regardless of the cause.',
      '11.4 Third-Party Services: We are NOT liable for any failure, change, downtime, fee increase, policy change, or termination by any third-party platform (AI builder, domain registrar, hosting provider, payment processor, AI model provider, social platform, search engine) that you use to apply what you learn.',
    ],
  },
  {
    n: '12',
    title: 'Force Majeure',
    body: [
      '12.1 Neither party shall be in breach of these Terms, nor liable for delay in performing or failure to perform any obligations under these Terms, if such delay or failure results from a Force Majeure Event.',
      '12.2 A "Force Majeure Event" includes any event beyond a party\'s reasonable control, including but not limited to: acts of God, war, terrorism, riots, embargoes, fire, flood, earthquake, severe weather, pandemic or epidemic, government-imposed lockdowns or restrictions, failure of internet service providers, failure of third-party platforms (including the AI builder we teach on), cyber-attacks, power outages, or industrial action.',
      '12.3 If a Force Majeure Event prevents Audio & Co. from delivering the Programme in its scheduled form, we will, at our discretion:',
      '(a) reschedule affected sessions to a reasonable alternative date;',
      '(b) substitute affected live sessions with equivalent recorded content;',
      '(c) where the Force Majeure Event continues for more than 60 days and prevents delivery of the Programme in any equivalent form, offer a pro-rata refund of any UNDELIVERED portion of the Programme.',
      '12.4 You acknowledge that no refund is due for delivered portions of the Programme, regardless of Force Majeure Events thereafter.',
    ],
  },
  {
    n: '13',
    title: 'Termination by Us',
    body: [
      '13.1 We may terminate or suspend your access to the Programme without refund if you:',
      '(a) materially breach these Terms (including but not limited to clauses 9 and 5.4);',
      '(b) fail to pay an instalment when due and do not cure within the 7-day period under clause 8.2;',
      '(c) act in a way that is harassing, threatening, defamatory, or harmful to other Delegates or staff;',
      '(d) attempt to circumvent the IP restrictions in clause 9.',
      '13.2 Termination under clause 13.1 does NOT relieve you of your obligation to pay any outstanding balance.',
    ],
  },
  {
    n: '14',
    title: 'Data Protection',
    body: [
      '14.1 We process your personal data (name, email, payment data via Wise, learning progress, community posts) in accordance with our Privacy Policy and UK GDPR.',
      '14.2 We use your data to deliver the Programme, communicate with you about it, process payment, and where you have consented, to send you marketing about future cohorts or Audio & Co. services. You can withdraw marketing consent at any time.',
      '14.3 We share your data with: our email provider (Brevo), our payment provider (Wise), our community platform (Skool / Circle / equivalent), and our hosting provider. We do not sell your data.',
      '14.4 You can request access, correction, or deletion of your personal data by emailing hello@audioandco.com.',
    ],
  },
  {
    n: '15',
    title: 'Changes to These Terms',
    body: [
      '15.1 We may update these Terms from time to time. The version that applies to your purchase is the version published on https://audioandco.com/training-terms at the time of your payment.',
      '15.2 If material changes are made AFTER your purchase that significantly disadvantage you, we will notify you by email and you may either accept the new Terms or request a pro-rata refund of any UNDELIVERED Programme content.',
    ],
  },
  {
    n: '16',
    title: 'Governing Law and Jurisdiction',
    body: [
      '16.1 These Terms are governed by the laws of England and Wales.',
      '16.2 Any dispute arising under or in connection with these Terms shall be subject to the EXCLUSIVE JURISDICTION of the courts of England and Wales, including for the purpose of small claims proceedings under clause 8.3.',
      '16.3 Nothing in this clause restricts your statutory rights as a UK consumer.',
    ],
  },
  {
    n: '17',
    title: 'Acceptance',
    body: [
      'By ticking the "I have read and accept the Training Terms & Conditions" box on the Training page, or by completing your payment via the Buy Now button on https://audioandco.com/training, you confirm that you have READ, UNDERSTOOD, and AGREE to be bound by these Terms in full.',
      'Last updated: ' + new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
    ],
  },
];

export default function TrainingTerms() {
  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="training-terms-page">
      <SEO
        title="Training Terms & Conditions | Audio & Co"
        description="UK consumer terms and conditions for the Audio & Co. Build Your Own Website & Mobile App With AI training programme. Governed by English law."
        noindex={false}
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <Link to="/training" className="inline-flex items-center gap-2 text-sm text-red-600 hover:underline mb-6" data-testid="training-terms-back">
          <ArrowLeft className="w-4 h-4" /> Back to Training Programme
        </Link>

        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 bg-red-50 border border-red-200 rounded-lg flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-red-600">UK Consumer Contract</p>
            <h1 className="text-2xl sm:text-3xl font-black text-gray-900">Training Programme Terms &amp; Conditions</h1>
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-10 leading-relaxed">
          These Terms apply to all purchases of the Audio &amp; Co. <em>Build Your Own Website &amp; Mobile App With AI</em>
          training programme. Please read them in full before purchasing. By completing payment you confirm
          your acceptance of these Terms.
        </p>

        <div className="space-y-8">
          {SECTIONS.map((s) => (
            <section key={s.n} className="border-l-2 border-red-600 pl-5">
              <h2 className="text-lg sm:text-xl font-black text-gray-900 mb-3">
                <span className="text-red-600">{s.n}.</span> {s.title}
              </h2>
              <div className="space-y-2 text-sm sm:text-base text-gray-700 leading-relaxed">
                {s.body.map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-gray-200 text-sm text-gray-500">
          <p className="mb-2"><strong>Questions?</strong> Email <a href="mailto:hello@audioandco.com" className="text-red-600 hover:underline">hello@audioandco.com</a> before you purchase if anything in these Terms is unclear.</p>
          <p>These Terms operate alongside our general <Link to="/terms" className="text-red-600 hover:underline">Website Terms</Link> and <Link to="/privacy" className="text-red-600 hover:underline">Privacy Policy</Link>.</p>
        </div>
      </div>
    </div>
  );
}
